import React, { useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { toast } from 'sonner';
import { Coffee, ArrowLeft, Shield } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Login illustration URL
const LOGIN_ILLUSTRATION = "https://static.prod-images.emergentagent.com/jobs/b4e419ab-e302-44b7-9ad7-697c067738c2/images/72a49a77787c314343023db664c035199987dd279cd6b5462d941d42d76a6af0.png";

const AdminLogin = ({ setUser }) => {
  const navigate = useNavigate();
  const [adminUsername, setAdminUsername] = useState('');
  const [adminPassword, setAdminPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleAdminLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/login`, {
        username: adminUsername,
        password: adminPassword
      });
      if (response.data.success) {
        setUser(response.data);
        toast.success('Admin login successful!');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 bg-gradient-to-br from-background via-muted to-background">
      
      {/* Back to Franchise Login Button */}
      <div className="absolute top-4 left-4 z-20">
        <Button
          onClick={() => navigate('/')}
          className="bg-white text-primary hover:bg-gray-100 rounded-full px-6 py-2 font-bold uppercase tracking-wide shadow-lg flex items-center gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Franchise Login
        </Button>
      </div>

      {/* Main Container - Split Layout for Desktop/Tablet */}
      <div className="w-full max-w-5xl flex items-center justify-center gap-8 lg:gap-12">
        
        {/* Illustration - Hidden on Mobile, Visible on md+ */}
        <div className="hidden md:flex flex-1 items-center justify-center p-4">
          <div className="relative w-full max-w-lg">
            {/* Decorative background blob */}
            <div className="absolute inset-0 bg-gradient-to-br from-[#3E2723]/5 to-[#8D6E63]/10 rounded-3xl transform -rotate-3" />
            <div className="relative">
              <img 
                src={LOGIN_ILLUSTRATION} 
                alt="Coffee Franchise Billing System" 
                className="w-full h-auto object-contain drop-shadow-xl rounded-2xl"
              />
              {/* Brand tagline */}
              <div className="mt-6 text-center">
                <h2 className="text-xl lg:text-2xl font-bold text-primary">
                  Centralized Franchise Management
                </h2>
                <p className="text-secondary mt-2 text-sm lg:text-base">
                  Monitor • Analyze • Control • All Franchises
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Login Card */}
        <Card className="w-full max-w-md relative z-10 shadow-2xl border-0 flex-shrink-0" data-testid="admin-login-card">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <img src="/logo.png" alt="1946 Logo" className="h-24 w-auto object-contain" />
            </div>
            <CardTitle className="text-2xl font-bold text-primary tracking-tight">Admin Portal</CardTitle>
            <CardDescription className="text-base mt-2">1946 Centralized Billing System</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleAdminLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="admin-username" className="text-sm font-medium">Username</Label>
                <Input
                  id="admin-username"
                  data-testid="admin-username-input"
                  type="text"
                  placeholder="Enter username"
                  value={adminUsername}
                  onChange={(e) => setAdminUsername(e.target.value)}
                  className="h-12 rounded-xl border-2 focus:border-accent"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="admin-password" className="text-sm font-medium">Password</Label>
                <Input
                  id="admin-password"
                  data-testid="admin-password-input"
                  type="password"
                  placeholder="Enter password"
                  value={adminPassword}
                  onChange={(e) => setAdminPassword(e.target.value)}
                  className="h-12 rounded-xl border-2 focus:border-accent"
                  required
                />
              </div>
              <Button
                type="submit"
                data-testid="admin-login-button"
                disabled={loading}
                className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-full text-base font-bold uppercase tracking-wider mt-6"
              >
                {loading ? 'Logging in...' : 'Login as Admin'}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminLogin;
